import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const DENOMINATIONS = [500, 200, 100, 50, 20, 10, 5, 2, 1];

export const denominationTotal = (counts = {}) =>
  DENOMINATIONS.reduce((sum, d) => sum + d * (parseInt(counts[d], 10) || 0), 0);

export const emptyDenominations = () => Object.fromEntries(DENOMINATIONS.map((d) => [d, '']));

/** Note-count grid used while closing the register. Calls onChange({500: '3', ...}). */
export const DenominationCounter = ({ value, onChange }) => {
  const total = denominationTotal(value);
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3" data-testid="denomination-counter">
      <Label className="text-gray-700">Cash denomination count</Label>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-2">
        {DENOMINATIONS.map((d) => {
          const n = parseInt(value[d], 10) || 0;
          return (
            <div key={d} className="bg-white border border-gray-200 rounded-md p-2">
              <div className="text-xs font-semibold text-gray-600">₹{d} ×</div>
              <Input
                type="number" min="0" step="1" inputMode="numeric"
                value={value[d] ?? ''}
                onChange={(e) => onChange({ ...value, [d]: e.target.value.replace(/[^\d]/g, '') })}
                className="h-8 mt-1 bg-gray-50 border-gray-300 text-right"
                data-testid={`denom-${d}`}
              />
              <div className="text-[11px] text-right text-gray-500 mt-1">= ₹{(d * n).toLocaleString('en-IN')}</div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between items-center mt-3 text-sm">
        <span className="text-gray-600">Counted total</span>
        <span className="font-bold text-gray-900" data-testid="denomination-total">₹{total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
      </div>
    </div>
  );
};

/** Read-only table for the closing report / history (print-friendly). */
export const DenominationTable = ({ denominations, compact = false }) => {
  if (!denominations) return null;
  const rows = DENOMINATIONS.filter((d) => (parseInt(denominations[d], 10) || 0) > 0);
  if (rows.length === 0) return null;
  return (
    <table className={`w-full ${compact ? 'text-xs' : 'text-sm'}`} data-testid="denomination-table">
      <thead>
        <tr className="border-b border-gray-300 text-gray-600">
          <th className="text-left py-1">Note</th>
          <th className="text-right py-1">Count</th>
          <th className="text-right py-1">Amount</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((d) => {
          const n = parseInt(denominations[d], 10) || 0;
          return (
            <tr key={d} className="border-b border-gray-100">
              <td className="py-1">₹{d}</td>
              <td className="py-1 text-right">{n}</td>
              <td className="py-1 text-right">₹{(d * n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr>
          );
        })}
        <tr className="font-semibold">
          <td className="py-1">Total</td>
          <td className="py-1 text-right">{rows.reduce((s, d) => s + (parseInt(denominations[d], 10) || 0), 0)}</td>
          <td className="py-1 text-right">₹{denominationTotal(denominations).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
        </tr>
      </tbody>
    </table>
  );
};
